import './styles/inspinia/_app.scss'

import { lazy } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client'
import { LoadingLayout, PageLayoutNotFound } from '@ztechapps/webui.page'
import { CenterScreen, ErrorContent } from '@ztechapps/webui.util'
import { AppContextProvider } from './hooks/useAppContext'
import { setupApolloClient } from './config/graphql'
import { routesDef } from './config/routes'
import { VerticalLayout } from './components/layout/VerticalLayout'
import { PageTitle } from './components/PageTitle'

// A chunk fetch can fail on a network blip, or when this tab is held open across a redeploy that
// moves the chunk hash — resolved as the lazy component instead of a rejection, so the route
// renders this instead of sitting on its loading fallback forever. Same fallback shape as
// apps/panelpunches/admin/src/app.tsx:71 and apps/quickscore/admin/src/app.tsx:30.
const RouteChunkFailed = () => (
  <CenterScreen>
    <ErrorContent message="This page failed to load. Please refresh the tab." />
  </CenterScreen>
)

const OverviewPage = lazy(() => import('./view/overview'))
const TicketListPage = lazy(() => import('./view/tickets/list'))
const TicketDetailPage = lazy(() => import('./view/tickets/item'))
const ManifestPage = lazy(() => import('./view/tickets/manifest'))
const TicketPickupAnalyzePage = lazy(() =>
  import('./view/tickets/analyze').then((m) => ({ default: m.TicketPickupAnalyzePage }))
)
const DriverListPage = lazy(() => import('./view/drivers/list'))
const DriverDetailPage = lazy(() => import('./view/drivers/item'))
const RouteListPage = lazy(() => import('./view/routes/list'))
const RouteDetailPage = lazy(() => import('./view/routes/item'))
const CustomerListPage = lazy(() => import('./view/customers/list'))
const CustomerDetailPage = lazy(() => import('./view/customers/item'))
const SettingsPage = lazy(() => import('./view/settings'))
const JoinPage = lazy(() => import('./view/account/join'))
const LoginPage = lazy(() => import('./view/account/login'))
const LoginSuccessPage = lazy(() => import('./view/account/login-success'))
const LoginFailedPage = lazy(() => import('./view/account/login-failed'))
const LoadingPage = lazy(() => import('./view/account/loading'))
const ChangePasswordPage = lazy(() => import('./view/account/change-password'))
const CreditPurchaseReturnPage = lazy(() =>
  import('./view/account/credit-purchase-return')
    .then((m) => ({ default: m.CreditPurchaseReturnPage }))
    .catch(() => ({ default: RouteChunkFailed }))
)

const apolloClient = setupApolloClient()

// Follows apps/panelpunches/store/src/pages/NotFound.tsx:5-12
const NotFoundPage = () => (
  <>
    <PageTitle title="Page Not Found" />
    <PageLayoutNotFound />
  </>
)

export const App = () => {
  return (
    <BrowserRouter>
      <ApolloProvider client={apolloClient}>
        <AppContextProvider>
          <Routes>
            <Route
              index
              element={
                <LoadingLayout>
                  <LoadingPage />
                </LoadingLayout>
              }
            />
            <Route
              path={routesDef.JoinPage.path}
              element={
                <LoadingLayout>
                  <JoinPage />
                </LoadingLayout>
              }
            />
            <Route
              path={routesDef.LoginPage.path}
              element={
                <LoadingLayout>
                  <LoginPage />
                </LoadingLayout>
              }
            />
            <Route
              path={routesDef.AuthSuccessPage.path}
              element={
                <LoadingLayout>
                  <LoginSuccessPage />
                </LoadingLayout>
              }
            />
            <Route
              path={routesDef.AuthFailedPage.path}
              element={
                <LoadingLayout>
                  <LoginFailedPage />
                </LoadingLayout>
              }
            />
            <Route
              path={routesDef.ChangePasswordPage.path}
              element={
                <LoadingLayout>
                  <ChangePasswordPage />
                </LoadingLayout>
              }
            />
            <Route
              path={routesDef.CreditPurchaseReturnPage.path}
              element={
                <LoadingLayout>
                  <CreditPurchaseReturnPage />
                </LoadingLayout>
              }
            />

            <Route path="/app" element={<VerticalLayout />}>
              <Route path={routesDef.OverviewPage.path} element={<OverviewPage />} />
              <Route path={routesDef.TicketListPage.path} element={<TicketListPage />} />
              <Route path={routesDef.TicketDetailPage.path} element={<TicketDetailPage />} />
              <Route path={routesDef.TicketManifestPage.path} element={<ManifestPage />} />
              <Route path={routesDef.TicketPickupAnalyzePage.path} element={<TicketPickupAnalyzePage />} />
              <Route path={routesDef.DriverListPage.path} element={<DriverListPage />} />
              <Route path={routesDef.DriverDetailPage.path} element={<DriverDetailPage />} />
              <Route path={routesDef.RouteListPage.path} element={<RouteListPage />} />
              <Route path={routesDef.RouteDetailPage.path} element={<RouteDetailPage />} />
              <Route path={routesDef.CustomerListPage.path} element={<CustomerListPage />} />
              <Route path={routesDef.CustomerDetailPage.path} element={<CustomerDetailPage />} />
              <Route path={routesDef.SettingsPage.path} element={<SettingsPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppContextProvider>
      </ApolloProvider>
    </BrowserRouter>
  )
}
